import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { BarChart, Bar, Line, PieChart, Pie, Cell, ComposedChart, ResponsiveContainer, Tooltip, Legend, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';
import { RefreshCw, Sparkles, Brain, AlertTriangle, Wrench, Clock, Zap, Target, Shield, ChevronDown, ChevronUp, Filter, BarChart2, AlertCircle, CheckCircle, Info, ArrowUpRight } from 'lucide-react';
import { fetchWorkOrderSummaryForAI } from '../../services/workOrderService';
import { getChatCompletion } from '../../services/aiIntegrations/chatCompletion';

import BreakdownBrain from './components/BreakdownBrain';
import Icon from '../../components/AppIcon';










// ── Color palette (futuristic neon) ──────────────────────────────────────────
const COLORS = ['#0891B2', '#7C3AED', '#F59E0B', '#10B981', '#EF4444', '#EC4899', '#3B82F6', '#64748B'];
const RISK_COLORS = { HIGH: '#EF4444', MEDIUM: '#F59E0B', LOW: '#10B981' };

// ── Helpers ───────────────────────────────────────────────────────────────────
function truncate(str, max = 18) {
  if (!str) return '';
  return str.length > max ? str.substring(0, max) + '…' : str;
}

function topNWithOthers(arr, labelKey, valueKey, n = 8) {
  if (!arr?.length) return [];
  const sorted = [...arr].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
  const top = sorted.slice(0, n);
  const rest = sorted.slice(n);
  if (rest.length > 0) {
    const othersVal = rest.reduce((s, r) => s + (r[valueKey] || 0), 0);
    if (othersVal > 0) {
      top.push({ [labelKey]: 'Others', [valueKey]: othersVal, _isOthers: true });
    }
  }
  return top;
}

function topNObjWithOthers(obj, n = 8) {
  if (!obj) return [];
  const entries = Object.entries(obj)
    .map(([k, v]) => ({ name: k, value: v }))
    .sort((a, b) => b.value - a.value);
  const top = entries.slice(0, n);
  const rest = entries.slice(n);
  if (rest.length > 0) {
    const othersVal = rest.reduce((s, r) => s + r.value, 0);
    if (othersVal > 0) top.push({ name: 'Others', value: othersVal });
  }
  return top;
}

function parseAIResponse(content) {
  const cleaned = content?.replace(/```json\n?/gi, '')?.replace(/```\n?/g, '')?.trim();
  try { return JSON.parse(cleaned); } catch {
    const obj = cleaned?.match(/\{[\s\S]*\}/);
    if (obj) { try { return JSON.parse(obj[0]); } catch { } }
    return null;
  }
}

function buildMasterPrompt(summary) {
  return `You are a senior industrial maintenance expert analyzing a plant's work order database.

CLEANED & NORMALIZED WORK ORDER SUMMARY (${summary?.total} total records):
- Total Breakdowns: ${summary?.breakdownCount} (${summary?.breakdownPct}% of all orders)
- Total Downtime: ${summary?.totalBreakdownTime} minutes
- MTTR: ${summary?.mttr} minutes
- Worst Equipment: ${summary?.worstEquipment}
- Top Root Cause: ${summary?.topRootCause}

TOP ROOT CAUSES (standardized):
${JSON.stringify(summary?.topRootCauses?.slice(0, 8), null, 2)}

TOP EQUIPMENT BY FAILURES:
${JSON.stringify(summary?.topEquipment?.slice(0, 8), null, 2)}

EQUIPMENT RISK SCORES:
${JSON.stringify(summary?.equipmentRisk?.slice(0, 6), null, 2)}

SHIFT ANALYSIS:
${JSON.stringify(summary?.shiftMTTR, null, 2)}

OUTLIER EVENTS (extreme downtime):
${JSON.stringify(summary?.outliers?.slice(0, 5), null, 2)}

Based ONLY on this data, return a JSON object with exactly these keys:
- "aiSummary": string (2-3 sentence executive summary with specific numbers)
- "insights": array of 4 objects, each with: title, description, severity (high|medium|low), category, impact, recommendation
- "predictions": array of 4 objects, each with: title, description, confidence (high|medium|low), impact (high|medium|low), timeframe, category, actionable
- "recommendations": array of 5 objects, each with: title, detail, priority (critical|high|medium), category

Rules: reference specific equipment IDs, root causes, and numbers from the data. Never use null or empty values.`;
}

// ── Custom Tooltip (futuristic) ───────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-cyan-200 rounded-lg shadow-xl shadow-cyan-100/50 p-3 text-sm max-w-xs z-50">
      <p className="font-semibold text-cyan-700 mb-2 text-xs leading-tight">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color || '#0891B2' }} className="text-xs py-0.5">
          {p?.name}: <span className="font-bold text-gray-700">{typeof p?.value === 'number' ? p?.value?.toLocaleString() : p?.value}</span>
        </p>
      ))}
    </div>
  );
};

const FullNameTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const fullName = payload[0]?.payload?.fullName || label;
  return (
    <div className="bg-white border border-cyan-200 rounded-lg shadow-xl shadow-cyan-100/50 p-3 text-sm max-w-xs z-50">
      <p className="font-semibold text-cyan-700 mb-2 text-xs leading-tight">{fullName}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color || '#0891B2' }} className="text-xs py-0.5">
          {p?.name}: <span className="font-bold text-gray-700">{typeof p?.value === 'number' ? p?.value?.toLocaleString() : p?.value}</span>
        </p>
      ))}
    </div>
  );
};

// ── Pie custom label ──────────────────────────────────────────────────────────
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#1e293b" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="bold">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Animated section wrapper ──────────────────────────────────────────────────
const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ── KPI Hero Card (futuristic light) ─────────────────────────────────────────
const HeroKPICard = ({ icon: Icon, label, value, sub, accentColor, glowColor }) => (
  <div className={`relative rounded-xl border ${accentColor} bg-white p-5 flex items-start gap-4 overflow-hidden group hover:scale-[1.03] transition-all duration-300 shadow-sm cursor-default`}
    style={{
      boxShadow: `0 2px 12px ${glowColor}18`,
      transition: 'box-shadow 0.3s ease, transform 0.3s ease',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 24px ${glowColor}40, 0 0 0 1.5px rgba(8,145,178,0.25)`; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 2px 12px ${glowColor}18`; }}
  >
    <div className="absolute top-0 right-0 w-20 h-20 rounded-bl-full opacity-5" style={{ background: glowColor }} />
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border"
      style={{ borderColor: glowColor + '50', background: glowColor + '12', color: glowColor }}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: glowColor + 'CC' }}>{label}</p>
      <p className="text-2xl font-bold text-gray-800 truncate">{value}</p>
      {sub && <p className="text-xs mt-0.5 truncate text-gray-600">{sub}</p>}
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, badge, accentColor = '#0891B2' }) => (
  <div className="flex items-start justify-between mb-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border"
        style={{ borderColor: accentColor + '40', background: accentColor + '12' }}>
        <Icon className="w-5 h-5" style={{ color: accentColor }} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {badge && (
      <span className="px-3 py-1 text-xs font-semibold rounded-full border flex-shrink-0"
        style={{ borderColor: accentColor + '50', color: accentColor, background: accentColor + '12' }}>{badge}</span>
    )}
  </div>
);

const AIBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border border-cyan-400/50 bg-cyan-50 text-cyan-600">
    <Sparkles className="w-3 h-3" /> AI
  </span>
);

// ── Chart wrapper (light futuristic) ─────────────────────────────────────────
const ChartCard = ({ title, subtitle, children }) => (
  <div
    className="bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 shadow-sm"
    style={{ transition: 'box-shadow 0.3s ease, border-color 0.3s ease' }}
    onMouseEnter={e => {
      e.currentTarget.style.boxShadow = '0 8px 32px rgba(8,145,178,0.14), 0 0 0 1.5px rgba(8,145,178,0.25)';
      e.currentTarget.style.borderColor = 'rgba(8,145,178,0.35)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.boxShadow = '0 1px 3px rgba(30,58,138,0.08)';
      e.currentTarget.style.borderColor = '#e2e8f0';
    }}
  >
    <h3 className="font-semibold text-cyan-700 text-sm mb-1">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500 mb-5">{subtitle}</p>}
    {children}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const WorkOrderAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);
  const [summary, setSummary] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filterShift, setFilterShift] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');
  const [filterEquipment, setFilterEquipment] = useState('ALL');
  const [filterRootCause, setFilterRootCause] = useState('ALL');

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAiError(false);
      const summaryData = await fetchWorkOrderSummaryForAI();
      if (!summaryData) { setSummary(null); return; }
      setSummary(summaryData);

      setAiLoading(true);
      try {
        const res = await getChatCompletion(
          'OPEN_AI', 'gpt-4.1-mini',
          [
            {
              role: 'system',
              content: 'You are an industrial maintenance AI analyst. Always respond with valid JSON only. No markdown, no explanation, no code fences — pure JSON object.'
            },
            { role: 'user', content: buildMasterPrompt(summaryData) }
          ],
          {
            max_completion_tokens: 6000,
            temperature: 0.1,
            response_format: { type: 'json_object' }
          }
        );
        if (res) {
          const content = res?.choices?.[0]?.message?.content
            || res?.message?.content
            || res?.content
            || '';
          if (!content || !content.trim()) {
            console.error('AI response was empty. Full response:', JSON.stringify(res));
            setAiError(true);
          } else {
            const parsed = parseAIResponse(content);
            if (parsed) {
              setAiData(parsed);
            } else {
              console.error('AI response could not be parsed:', content?.substring(0, 200));
              setAiError(true);
            }
          }
        } else {
          console.error('AI call returned null/undefined');
          setAiError(true);
        }
      } catch (aiErr) {
        console.error('AI generation error:', aiErr);
        setAiError(true);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setAiLoading(false);
    }
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  const handleRefresh = () => { setRefreshing(true); loadAnalytics(); };

  // ── Chart data ────────────────────────────────────────────────────────────
  const monthlyTrendData = useMemo(() => {
    if (!summary?.byMonth) return [];
    return Object.entries(summary?.byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, count]) => ({
        month: month.length > 7 ? month.substring(2) : month,
        fullMonth: month,
        count,
        downtime: Math.round(summary?.monthlyDowntime?.[month] || 0),
      }));
  }, [summary]);

  const equipmentChartData = useMemo(() => {
    if (!summary?.topEquipment) return [];
    const sorted = [...summary.topEquipment].sort((a, b) => (b.total_downtime || 0) - (a.total_downtime || 0));
    const top10 = sorted.slice(0, 10);
    const rest = sorted.slice(10);
    const result = top10.map(e => ({
      name: truncate(e.equipment_id, 15),
      fullName: e.equipment_id,
      failures: e.count,
      downtime: e.total_downtime,
    }));
    if (rest.length > 0) {
      result.push({
        name: 'Others',
        fullName: `Others (${rest.length} equipment)`,
        failures: rest.reduce((s, r) => s + r.count, 0),
        downtime: rest.reduce((s, r) => s + (r.total_downtime || 0), 0),
      });
    }
    return result;
  }, [summary]);

  const rootCauseParetoData = useMemo(() => {
    if (!summary?.paretoRootCauses) return [];
    const sorted = [...summary.paretoRootCauses].sort((a, b) => (b.total_downtime || 0) - (a.total_downtime || 0));
    const top8 = sorted.slice(0, 8);
    const rest = sorted.slice(8);
    const items = [...top8];
    if (rest.length > 0) {
      items.push({
        root_cause: 'Others',
        total_downtime: rest.reduce((s, r) => s + (r.total_downtime || 0), 0),
        count: rest.reduce((s, r) => s + r.count, 0),
      });
    }
    let cumulative = 0;
    const total = items.reduce((s, r) => s + r.total_downtime, 0) || 1;
    return items.map(r => {
      cumulative += r.total_downtime;
      return {
        name: truncate(r.root_cause, 16),
        fullName: r.root_cause,
        downtime: r.total_downtime,
        count: r.count,
        cumPct: parseFloat(((cumulative / total) * 100).toFixed(1)),
      };
    });
  }, [summary]);

  const rootCausePieData = useMemo(() => {
    if (!summary?.topRootCauses) return [];
    return topNWithOthers(summary.topRootCauses, 'root_cause', 'count', 5).map(r => ({
      name: truncate(r.root_cause, 18),
      fullName: r.root_cause,
      value: r.count,
    }));
  }, [summary]);

  const shiftChartData = useMemo(() => {
    if (!summary?.byShift) return [];
    return Object.entries(summary?.byShift).map(([shift, count]) => ({
      shift: `Shift ${shift}`,
      failures: count,
      downtime: Math.round(summary?.shiftDowntime?.[shift] || 0),
      mttr: parseFloat(summary?.shiftMTTR?.[shift] || 0),
    }));
  }, [summary]);

  const spareChartData = useMemo(() => {
    if (!summary?.spareMap) return [];
    return topNObjWithOthers(summary.spareMap, 5);
  }, [summary]);

  const typeChartData = useMemo(() => {
    if (!summary?.byType) return [];
    return topNObjWithOthers(summary.byType, 6);
  }, [summary]);

  const inchargeData = useMemo(() => {
    if (!summary?.inchargeMTTR) return [];
    return [...summary.inchargeMTTR]
      .sort((a, b) => b.mttr - a.mttr)
      .slice(0, 8)
      .map(d => ({ ...d, name: truncate(d.name, 14), fullName: d.name }));
  }, [summary]);

  const shiftOptions = useMemo(() => ['ALL', ...Object.keys(summary?.byShift || {})], [summary]);
  const typeOptions = useMemo(() => ['ALL', ...Object.keys(summary?.byType || {})], [summary]);
  const equipmentOptions = useMemo(() => ['ALL', ...(summary?.topEquipment?.slice(0, 20)?.map(e => e?.equipment_id) || [])], [summary]);
  const rootCauseOptions = useMemo(() => ['ALL', ...(summary?.topRootCauses?.slice(0, 15)?.map(r => r?.root_cause) || [])], [summary]);

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Breakdown Analytics', path: '/work-order-analytics' },
  ];

  const hasData = !!summary;

  const severityStyle = (s) => ({
    high: { badge: 'bg-red-100 text-red-600 border border-red-200', border: 'border-l-red-500', icon: 'text-red-500' },
    medium: { badge: 'bg-amber-100 text-amber-600 border border-amber-200', border: 'border-l-amber-500', icon: 'text-amber-500' },
    low: { badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200', border: 'border-l-emerald-500', icon: 'text-emerald-500' },
    critical: { badge: 'bg-red-100 text-red-600 border border-red-200', border: 'border-l-red-600', icon: 'text-red-600' },
  }[s] || { badge: 'bg-cyan-100 text-cyan-600 border border-cyan-200', border: 'border-l-cyan-500', icon: 'text-cyan-500' });

  const priorityStyle = (p) => ({
    critical: 'bg-red-100 text-red-600 border border-red-200',
    high: 'bg-orange-100 text-orange-600 border border-orange-200',
    medium: 'bg-amber-100 text-amber-600 border border-amber-200',
  }[p] || 'bg-cyan-100 text-cyan-600 border border-cyan-200');

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #f0f7ff 40%, #eef2ff 100%)' }}>
      {/* Subtle dot-grid + diagonal line background pattern */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          backgroundImage: `
            radial-gradient(circle, rgba(8,145,178,0.22) 1.5px, transparent 1.5px),
            linear-gradient(rgba(8,145,178,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(8,145,178,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '28px 28px, 56px 56px, 56px 56px',
        }}
      />
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 relative z-10">
        <Breadcrumb items={breadcrumbItems} />

        {/* ── Page Header ── */}
        <Reveal delay={0}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-cyan-300 bg-cyan-50"
                style={{ boxShadow: '0 0 16px rgba(8,145,178,0.15)' }}>
                <Brain className="w-5 h-5 text-cyan-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800" style={{ textShadow: '0 0 30px rgba(0,245,255,0.3)' }}>Breakdown Intelligence</h1>
              <AIBadge />
            </div>
            <p className="text-gray-600 text-sm ml-1">AI-powered manufacturing analytics — root cause intelligence, predictions & recommendations</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all disabled:opacity-50 flex-shrink-0 shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        </Reveal>

        {/* ── Loading ── */}
        {loading && !refreshing ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center shadow-sm">
            <div className="w-16 h-16 rounded-2xl border border-cyan-200 bg-cyan-50 flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-cyan-500 animate-pulse" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Analyzing Work Orders...</h3>
            <p className="text-gray-600">Fetching all records, cleaning data, and generating AI intelligence.</p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border-2 border-red-200 bg-white p-12 text-center shadow-sm">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-600 mb-2">Error Loading Analytics</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={handleRefresh} className="px-6 py-2 rounded-xl border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all">Try Again</button>
          </div>
        ) : !hasData ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center shadow-sm">
            <BarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Data Found</h3>
            <p className="text-gray-600 mb-4">No work orders found. Please upload data via Settings → Work Order Import.</p>
            <button onClick={handleRefresh} className="px-6 py-2 rounded-xl border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all">Try Again</button>
          </div>
        ) : (
          <>
            {/* ── HERO KPI CARDS ── */}
            <Reveal delay={60}>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <HeroKPICard icon={Clock} label="Total Downtime" value={`${(summary?.totalBreakdownTime / 60)?.toFixed(0)}h`} sub={`${summary?.totalBreakdownTime?.toLocaleString()} mins`} accentColor="border-red-200" glowColor="#EF4444" />
              <HeroKPICard icon={Wrench} label="MTTR" value={`${summary?.mttr} min`} sub="Mean Time to Repair" accentColor="border-amber-200" glowColor="#F59E0B" />
              <HeroKPICard icon={Target} label="Top Root Cause" value={truncate(summary?.topRootCause, 16)} sub={`${summary?.topRootCauses?.[0]?.pct}% of failures`} accentColor="border-orange-200" glowColor="#F97316" />
              <HeroKPICard icon={AlertTriangle} label="Worst Equipment" value={summary?.worstEquipment} sub="Highest downtime" accentColor="border-cyan-200" glowColor="#0891B2" />
            </div>
            </Reveal>

            {/* ── AI SUMMARY ── */}
            {(aiData?.aiSummary || aiLoading) && (
              <Reveal delay={100}>
              <div className="rounded-2xl p-6 relative overflow-hidden border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 shadow-sm">
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-cyan-600" />
                    <span className="text-cyan-700 font-semibold text-sm uppercase tracking-widest">AI Executive Summary</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-cyan-300/60 to-transparent ml-2" />
                  </div>
                  {aiLoading && !aiData?.aiSummary ? (
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-cyan-300 border-t-cyan-500 rounded-full animate-spin" />
                      <span className="text-gray-600 text-sm">Generating intelligence...</span>
                    </div>
                  ) : (
                    <p className="text-gray-700 text-base leading-relaxed">{aiData?.aiSummary}</p>
                  )}
                </div>
              </div>
              </Reveal>
            )}

            {/* ── FILTERS ── */}
            <Reveal delay={120}>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <button
                onClick={() => setFiltersOpen(f => !f)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-cyan-600" />
                  <span className="font-semibold text-gray-700">Filters</span>
                  <span className="text-xs text-gray-500">(applies to charts below)</span>
                </div>
                {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>
              {filtersOpen && (
                <div className="px-6 pb-5 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-slate-100">
                  {[
                    { label: 'Shift', value: filterShift, set: setFilterShift, opts: shiftOptions },
                    { label: 'Work Order Type', value: filterType, set: setFilterType, opts: typeOptions },
                    { label: 'Equipment', value: filterEquipment, set: setFilterEquipment, opts: equipmentOptions },
                    { label: 'Root Cause', value: filterRootCause, set: setFilterRootCause, opts: rootCauseOptions },
                  ].map(f => (
                    <div key={f.label}>
                      <label className="block text-xs font-semibold text-cyan-700 mb-1 mt-4 uppercase tracking-wider">{f.label}</label>
                      <select
                        value={f.value}
                        onChange={e => f.set(e.target.value)}
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                      >
                        {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </Reveal>

            {/* ── ROOT CAUSE ANALYSIS ── */}
            <Reveal delay={0}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-cyan-200 transition-all duration-300 shadow-sm"
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(8,145,178,0.12), 0 0 0 1.5px rgba(8,145,178,0.22)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
            >
              <SectionHeader icon={Brain} title="Root Cause Intelligence" subtitle="Standardized failure categories with downtime impact" badge="CORE FEATURE" accentColor="#0891B2" />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* A. Root Cause Distribution */}
                <div>
                  <h3 className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-4">A. Root Cause Distribution (Top 5)</h3>
                  <p className="text-xs text-gray-500 mb-4">Hover slices for full category names</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={rootCausePieData}
                        cx="50%" cy="50%"
                        outerRadius={110}
                        dataKey="value"
                        labelLine={false}
                        label={renderPieLabel}
                      >
                        {rootCausePieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<FullNameTooltip />} />
                      <Legend
                        formatter={(v, e) => (
                          <span style={{ fontSize: 11, color: '#475569' }}>
                            {truncate(e?.payload?.fullName || v, 22)}
                          </span>
                        )}
                        wrapperStyle={{ paddingTop: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Root Cause Rankings Table */}
                <div>
                  <h3 className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-4">Root Cause Rankings</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {summary?.topRootCauses?.slice(0, 10)?.map((rc, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-cyan-50 border border-slate-200 hover:border-cyan-200 transition-all">
                        <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 border border-cyan-300 text-cyan-700 bg-cyan-50">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-700 truncate" title={rc?.root_cause}>{rc?.root_cause}</p>
                          <p className="text-xs text-gray-600">{rc?.count} failures · {rc?.total_downtime?.toLocaleString()} min</p>
                        </div>
                        <span className="text-xs font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full flex-shrink-0">{rc?.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* B. Pareto */}
              <div className="mt-8">
                <h3 className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-1">B. Pareto Analysis — Top Contributors to Downtime</h3>
                <p className="text-xs text-gray-500 mb-5">Top 8 root causes + Others · Hover bars for full names</p>
                <ResponsiveContainer width="100%" height={rootCauseParetoData.length * 42 + 60}>
                  <ComposedChart data={rootCauseParetoData} layout="vertical" margin={{ left: 8, right: 60, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={v => v.toLocaleString()} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#475569' }} width={130} />
                    <Tooltip content={<FullNameTooltip />} />
                    <ReferenceLine y={80} stroke="#EF4444" strokeDasharray="5 5" label={{ value: '80%', fill: '#EF4444', fontSize: 10, position: 'right' }} />
                    <Line type="monotone" dataKey="cumPct" name="Cumulative %" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B' }} />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">Cumulative % of total downtime</p>
                  <ResponsiveContainer width="100%" height={120}>
                    <ComposedChart data={rootCauseParetoData} margin={{ left: 8, right: 60, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} angle={-30} textAnchor="end" height={50} interval={0} />
                      <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: '#475569' }} width={36} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Cumulative']} />
                      <ReferenceLine y={80} stroke="#EF4444" strokeDasharray="5 5" label={{ value: '80%', fill: '#EF4444', fontSize: 10, position: 'right' }} />
                      <Line type="monotone" dataKey="cumPct" name="Cumulative %" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Root Cause × Equipment Matrix */}
              {summary?.rcEquipmentMatrix && Object.keys(summary?.rcEquipmentMatrix)?.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-4">C. Root Cause × Equipment Mapping</h3>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left px-4 py-3 font-semibold text-cyan-700 min-w-[160px]">Root Cause</th>
                          {summary?.topEquipment?.slice(0, 8)?.map(e => (
                            <th key={e?.equipment_id} className="px-3 py-3 font-semibold text-cyan-700 text-center min-w-[64px]" title={e?.equipment_id}>
                              {truncate(e?.equipment_id, 8)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(summary?.rcEquipmentMatrix)?.slice(0, 8)?.map(([rc, eqMap], i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                            <td className="px-4 py-2.5 font-semibold text-gray-700 text-xs" title={rc}>{truncate(rc, 26)}</td>
                            {summary?.topEquipment?.slice(0, 8)?.map(e => {
                              const count = eqMap?.[e?.equipment_id] || 0;
                              return (
                                <td key={e?.equipment_id} className="px-3 py-2.5 text-center">
                                  {count > 0 ? (
                                    <span className={`inline-flex w-7 h-7 rounded-lg text-xs font-bold items-center justify-center ${count >= 5 ? 'bg-red-100 text-red-600 border border-red-200' : count >= 3 ? 'bg-amber-100 text-amber-600 border border-amber-200' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'}`}>
                                      {count}
                                    </span>
                                  ) : <span className="text-gray-400">—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            </Reveal>

            {/* ── AI INSIGHTS ── */}
            {hasData && (
              <Reveal delay={0}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-amber-200 transition-all duration-300 shadow-sm"
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(8,145,178,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                <div className="flex items-center justify-between mb-6">
                  <SectionHeader icon={Sparkles} title="AI Insights" subtitle="Smart analysis from cleaned & grouped data" accentColor="#A855F7" />
                  {aiLoading && (
                    <div className="flex items-center gap-2 text-sm text-purple-600 flex-shrink-0">
                      <div className="w-3 h-3 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
                      Analyzing...
                    </div>
                  )}
                </div>
                {aiLoading && !aiData?.insights?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />)}
                  </div>
                ) : aiError && !aiData?.insights?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                    <AlertCircle className="w-8 h-8 text-amber-400" />
                    <p className="text-sm text-gray-600 text-center">AI insights could not be generated. This may be a temporary issue.</p>
                    <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 transition-all text-sm">
                      <RefreshCw className="w-3.5 h-3.5" /> Retry AI Analysis
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiData?.insights?.map((insight, i) => {
                      const s = severityStyle(insight?.severity);
                      return (
                        <div key={i} className={`border-l-4 ${s.border} bg-slate-50 rounded-r-xl p-4 hover:bg-white transition-all border border-slate-200 hover:border-slate-300 shadow-sm`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-semibold text-gray-800 text-sm leading-tight">{insight?.title}</h3>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${s.badge}`}>{insight?.severity}</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed mb-3">{insight?.description}</p>
                          {insight?.impact && (
                            <div className="flex items-center gap-1.5 mb-2">
                              <ArrowUpRight className={`w-3 h-3 ${s.icon}`} />
                              <span className="text-xs font-semibold text-gray-700">{insight?.impact}</span>
                            </div>
                          )}
                          {insight?.recommendation && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-amber-700 font-medium flex items-start gap-1.5">
                                <span className="flex-shrink-0">💡</span>
                                {insight?.recommendation}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </Reveal>
            )}

            {/* ── AI PREDICTIONS ── */}
            {hasData && (
              <Reveal delay={0}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-amber-200 transition-all duration-300 shadow-sm"
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(245,158,11,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                <SectionHeader icon={Zap} title="AI Predictions" subtitle="Next likely failures based on historical patterns" badge="WOW FACTOR" accentColor="#F59E0B" />
                {aiLoading && !aiData?.predictions?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map(i => <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />)}
                  </div>
                ) : aiError && !aiData?.predictions?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                    <AlertCircle className="w-8 h-8 text-amber-400" />
                    <p className="text-sm text-gray-600 text-center">AI predictions could not be generated. This may be a temporary issue.</p>
                    <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all text-sm">
                      <RefreshCw className="w-3.5 h-3.5" /> Retry AI Analysis
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiData?.predictions?.map((pred, i) => {
                      const s = severityStyle(pred?.impact);
                      const confColor = { high: 'bg-emerald-100 text-emerald-700 border border-emerald-200', medium: 'bg-amber-100 text-amber-700 border border-amber-200', low: 'bg-slate-100 text-gray-600 border border-slate-200' }[pred?.confidence] || 'bg-slate-100 text-gray-600 border border-slate-200';
                      return (
                        <div key={i} className={`border-l-4 ${s.border} bg-slate-50 rounded-r-xl p-4 hover:bg-white transition-all border border-slate-200 hover:border-emerald-200 shadow-sm`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-semibold text-gray-800 text-sm leading-tight">{pred?.title}</h3>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${confColor}`}>{pred?.confidence} conf.</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed mb-3">{pred?.description}</p>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            {pred?.timeframe && (
                              <span className="text-xs bg-slate-100 text-gray-600 border border-slate-200 px-2 py-0.5 rounded-full font-medium">⏱ {pred?.timeframe}</span>
                            )}
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.badge}`}>{pred?.impact} impact</span>
                          </div>
                          {pred?.actionable && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-amber-700 font-medium flex items-start gap-1.5">
                                <span className="flex-shrink-0">⚡</span>
                                {pred?.actionable}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </Reveal>
            )}

            {/* ── AI RECOMMENDATIONS ── */}
            {hasData && (
              <Reveal delay={0}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-200 transition-all duration-300 shadow-sm"
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(16,185,129,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                <SectionHeader icon={CheckCircle} title="AI Recommendations" subtitle="Highly specific actions based on root cause analysis" accentColor="#10B981" />
                {aiLoading && !aiData?.recommendations?.length ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
                  </div>
                ) : aiError && !aiData?.recommendations?.length ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                    <AlertCircle className="w-8 h-8 text-emerald-400" />
                    <p className="text-sm text-gray-600 text-center">AI recommendations could not be generated. This may be a temporary issue.</p>
                    <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all text-sm">
                      <RefreshCw className="w-3.5 h-3.5" /> Retry AI Analysis
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {aiData?.recommendations?.map((rec, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-white transition-all border border-slate-200 hover:border-emerald-200 shadow-sm">
                        <span className={`text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0 ${priorityStyle(rec?.priority)}`}>{rec?.priority?.toUpperCase()}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm">{rec?.title}</p>
                          <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{rec?.detail}</p>
                        </div>
                        {rec?.category && (
                          <span className="text-xs bg-slate-100 text-gray-600 border border-slate-200 px-2 py-0.5 rounded-full flex-shrink-0 capitalize">{rec?.category}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </Reveal>
            )}

            {/* ── VISUAL ANALYTICS ── */}
            <Reveal delay={0}>
            <div>
              <SectionHeader icon={BarChart2} title="Visual Analytics" subtitle="Comprehensive breakdown across all dimensions" accentColor="#0891B2" />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* A. Time Trend */}
                {monthlyTrendData?.length > 0 && (
                  <ChartCard title="A. Time Trend — Breakdowns & Downtime" subtitle="Monthly breakdown count and total downtime">
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={monthlyTrendData} margin={{ bottom: 20, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#475569' }} angle={-30} textAnchor="end" height={50} interval={0} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#475569' }} width={36} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#475569' }} width={40} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, color: '#475569' }} />
                        <Bar yAxisId="left" dataKey="count" name="Breakdowns" fill="#7C3AED" radius={[3, 3, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="downtime" name="Downtime (min)" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* B. Equipment Analysis */}
                {equipmentChartData?.length > 0 && (
                  <ChartCard title="B. Equipment Analysis — Top 10 by Downtime" subtitle="Sorted by total downtime · Hover for full names">
                    <ResponsiveContainer width="100%" height={equipmentChartData.length * 38 + 40}>
                      <BarChart data={equipmentChartData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={v => v.toLocaleString()} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={100} />
                        <Tooltip content={<FullNameTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, color: '#475569' }} />
                        <Bar dataKey="failures" name="Failures" fill="#0891B2" radius={[0, 3, 3, 0]} barSize={14} />
                        <Bar dataKey="downtime" name="Downtime (min)" fill="#F59E0B" radius={[0, 3, 3, 0]} barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* D. Shift Analysis */}
                {shiftChartData?.length > 0 && (
                  <ChartCard title="D. Shift Analysis — Failures & MTTR" subtitle="Shift-wise breakdown count and mean repair time">
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={shiftChartData} margin={{ bottom: 8, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="shift" tick={{ fontSize: 12, fill: '#475569' }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#475569' }} width={36} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#475569' }} width={40} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, color: '#475569' }} />
                        <Bar yAxisId="left" dataKey="failures" name="Failures" fill="#8B5CF6" radius={[3, 3, 0, 0]} />
                        <Bar yAxisId="left" dataKey="downtime" name="Downtime (min)" fill="#06B6D4" radius={[3, 3, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="mttr" name="MTTR (min)" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* E. Incharge Performance */}
                {inchargeData?.length > 0 && (
                  <ChartCard title="E. Incharge Performance — MTTR" subtitle="Top 8 by highest MTTR · Hover for full names">
                    <ResponsiveContainer width="100%" height={inchargeData.length * 40 + 40}>
                      <BarChart data={inchargeData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#475569' }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={110} />
                        <Tooltip content={<FullNameTooltip />} />
                        <Bar dataKey="mttr" name="MTTR (min)" radius={[0, 4, 4, 0]} barSize={18}>
                          {inchargeData.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? '#EF4444' : i === 1 ? '#F59E0B' : '#10B981'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* F. Spare Usage */}
                {spareChartData?.length > 0 && (
                  <ChartCard title="F. Spare Parts Usage" subtitle="Valid entries only · Hover for details">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={spareChartData}
                          cx="50%" cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          labelLine={false}
                          label={renderPieLabel}
                        >
                          {spareChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          formatter={v => <span style={{ fontSize: 11, color: '#475569' }}>{truncate(v, 20)}</span>}
                          wrapperStyle={{ paddingTop: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* Work Order Type Distribution */}
                {typeChartData?.length > 0 && (
                  <ChartCard title="Work Order Type Distribution" subtitle="Breakdown vs Preventive and other types">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={typeChartData}
                          cx="50%" cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          labelLine={false}
                          label={renderPieLabel}
                        >
                          {typeChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          formatter={v => <span style={{ fontSize: 11, color: '#475569' }}>{truncate(v, 20)}</span>}
                          wrapperStyle={{ paddingTop: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>
            </div>
            </Reveal>

            {/* ── EQUIPMENT RISK SCORING ── */}
            {summary?.equipmentRisk?.length > 0 && (
              <Reveal delay={0}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-red-200 transition-all duration-300 shadow-sm"
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(239,68,68,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                <SectionHeader icon={Shield} title="Equipment Risk Scoring" subtitle="Risk classification based on failure frequency and downtime" accentColor="#EF4444" />
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left px-4 py-3 font-semibold text-cyan-700">Equipment ID</th>
                        <th className="text-center px-4 py-3 font-semibold text-cyan-700">Failures</th>
                        <th className="text-center px-4 py-3 font-semibold text-cyan-700">Total Downtime</th>
                        <th className="text-center px-4 py-3 font-semibold text-cyan-700">Risk Score</th>
                        <th className="text-center px-4 py-3 font-semibold text-cyan-700">Risk Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary?.equipmentRisk?.map((eq, i) => (
                        <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'} transition-colors cursor-default`}
                          style={{ transition: 'background 0.2s ease' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(8,145,178,0.06)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : 'rgba(248,250,252,0.6)'; }}
                        >
                          <td className="px-4 py-3 font-semibold text-gray-800">{eq?.equipment_id}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{eq?.count}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{eq?.total_downtime?.toLocaleString()} min</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${Math.min(eq?.risk_score, 100)}%`, backgroundColor: RISK_COLORS[eq?.risk_level] }} />
                              </div>
                              <span className="text-xs font-bold text-gray-700">{eq?.risk_score}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-xs font-bold px-3 py-1 rounded-full border" style={{ backgroundColor: RISK_COLORS[eq?.risk_level] + '18', color: RISK_COLORS[eq?.risk_level], borderColor: RISK_COLORS[eq?.risk_level] + '40' }}>
                              {eq?.risk_level}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              </Reveal>
            )}

            {/* ── OUTLIER DETECTION ── */}
            {summary?.outliers?.length > 0 && (
              <Reveal delay={0}>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-red-200 transition-all duration-300 shadow-sm"
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(239,68,68,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
              >
                <SectionHeader icon={AlertCircle} title="G. Outlier Detection" subtitle="Extreme downtime events (> 2 standard deviations from mean)" accentColor="#EF4444" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary?.outliers?.map((o, i) => (
                    <div key={i} className="border-l-4 border-l-red-400 bg-red-50 rounded-r-xl p-4 border border-red-100 hover:border-red-200 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-gray-800 text-sm">{o?.equipment_id}</span>
                        <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-bold">{o?.breakdown_time_mins} min</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-1">📅 {o?.date}</p>
                      <p className="text-xs text-red-600 font-semibold" title={o?.root_cause}>{truncate(o?.root_cause, 40)}</p>
                    </div>
                  ))}
                </div>
              </div>
              </Reveal>
            )}

            {/* ── DATA QUALITY FOOTER ── */}
            <Reveal delay={0}>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(8,145,178,0.10)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-4 h-4 text-cyan-600" />
                <span className="text-sm font-semibold text-gray-700">Data Quality Summary</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50">
                  <p className="text-2xl font-bold text-cyan-700">{summary?.total?.toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">Total Records</p>
                </div>
                <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50">
                  <p className="text-2xl font-bold text-emerald-700">{summary?.topRootCauses?.length}</p>
                  <p className="text-xs text-gray-600 mt-1">Root Cause Categories</p>
                </div>
                <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
                  <p className="text-2xl font-bold text-amber-700">{summary?.topEquipment?.length}</p>
                  <p className="text-xs text-gray-600 mt-1">Equipment Tracked</p>
                </div>
                <div className="p-3 rounded-xl border border-purple-200 bg-purple-50">
                  <p className="text-2xl font-bold text-purple-700">{Object.keys(summary?.byMonth || {}).length}</p>
                  <p className="text-xs text-gray-600 mt-1">Months of Data</p>
                </div>
              </div>
            </div>
            </Reveal>
          </>
        )}
      </div>

      {/* ── BREAKDOWN BRAIN AI ASSISTANT ── */}
      <BreakdownBrain
        summary={summary}
        activeFilters={{
          Shift: filterShift,
          Type: filterType,
          Equipment: filterEquipment,
          'Root Cause': filterRootCause,
        }}
      />
    </div>
  );
};

export default WorkOrderAnalytics;